import React, { useEffect, useState } from "react";
import api, { fmtDate, fmtINR } from "@/lib/api";
import { PageHeader, DataTable, PrimaryButton } from "@/components/DataViews";
import StatusBadge from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Plus, Check, Banknote } from "lucide-react";
import ExportButton from "@/components/ExportButton";

export default function PVsList() {
  const [items, setItems] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [open, setOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [payForm, setPayForm] = useState({ utr_no: "", cheque_no: "" });
  const [form, setForm] = useState({ invoice_id: "", tds_pct: 0, retention_pct: 0, bank: "", payment_mode: "NEFT", due_date: "", remarks: "" });
  const { hasRole } = useAuth();

  const load = () => api.get("/pvs").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/invoices").then((r) => setInvoices(r.data.filter((i) => i.status === "Verified")));
  }, []);

  const canCreate = hasRole("Accounts Executive", "Accounts Manager", "Administrator");
  const canApprove = hasRole("Accounts Manager", "Administrator");

  const save = async () => {
    try { const { data } = await api.post("/pvs", form); toast.success(`PV ${data.pv_no} created`); setOpen(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const approve = async (id) => { try { await api.post(`/pvs/${id}/approve`); toast.success("Approved"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const pay = async () => {
    try { await api.post(`/pvs/${payTarget}/pay`, payForm); toast.success("Marked as Paid"); setPayOpen(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="pvs-page">
      <PageHeader
        title="Payment Vouchers"
        subtitle="Vendor payment orchestration with TDS, retention, and bank details."
        actions={
          <>
            <ExportButton entity="pvs" filename="payment-vouchers.xlsx" />
            {canCreate && <PrimaryButton onClick={() => { setForm({ invoice_id: "", tds_pct: 0, retention_pct: 0, bank: "", payment_mode: "NEFT", due_date: "", remarks: "" }); setOpen(true); }} data-testid="new-pv-btn"><Plus size={14} className="mr-2" />New PV</PrimaryButton>}
          </>
        }
      />
      <DataTable
        testid="pvs-table"
        columns={[
          { key: "pv_no", label: "PV No.", render: (r) => <span className="font-mono text-xs font-semibold">{r.pv_no}</span> },
          { key: "vendor_name", label: "Vendor" },
          { key: "invoice_no", label: "Invoice", render: (r) => <span className="font-mono text-xs">{r.invoice_no}</span> },
          { key: "invoice_amount", label: "Invoice Amt", render: (r) => <span className="font-mono">{fmtINR(r.invoice_amount)}</span> },
          { key: "tds_amount", label: "TDS", render: (r) => <span className="font-mono text-red-700">-{fmtINR(r.tds_amount)}</span> },
          { key: "retention_amount", label: "Retention", render: (r) => <span className="font-mono text-red-700">-{fmtINR(r.retention_amount)}</span> },
          { key: "net_payable", label: "Net Payable", render: (r) => <span className="font-mono font-bold">{fmtINR(r.net_payable)}</span> },
          { key: "status", label: "Status", render: (r) => (
            <div className="flex items-center gap-2">
              <StatusBadge status={r.status} />
              {r.status === "Draft" && canApprove && <Button size="sm" variant="outline" onClick={() => approve(r.id)} data-testid={`approve-pv-${r.id}`}><Check size={12} className="mr-1" />Approve</Button>}
              {r.status === "Approved" && canApprove && <Button size="sm" variant="outline" onClick={() => { setPayTarget(r.id); setPayOpen(true); }} data-testid={`pay-pv-${r.id}`}><Banknote size={12} className="mr-1" />Mark Paid</Button>}
            </div>
          )},
        ]}
        data={items}
        empty="No payment vouchers yet."
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Payment Voucher</DialogTitle></DialogHeader>
          <div>
            <Label className="text-xs uppercase font-semibold">Invoice *</Label>
            <Select value={form.invoice_id} onValueChange={(v) => setForm({ ...form, invoice_id: v })}>
              <SelectTrigger data-testid="pv-invoice"><SelectValue placeholder="Select verified invoice" /></SelectTrigger>
              <SelectContent>{invoices.map((i) => <SelectItem key={i.id} value={i.id}>{i.invoice_no} — {i.vendor_name} — {fmtINR(i.invoice_amount)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase font-semibold">TDS %</Label><Input type="number" value={form.tds_pct} onChange={(e) => setForm({ ...form, tds_pct: parseFloat(e.target.value) || 0 })} data-testid="pv-tds" /></div>
            <div><Label className="text-xs uppercase font-semibold">Retention %</Label><Input type="number" value={form.retention_pct} onChange={(e) => setForm({ ...form, retention_pct: parseFloat(e.target.value) || 0 })} /></div>
            <div><Label className="text-xs uppercase font-semibold">Bank</Label><Input value={form.bank} onChange={(e) => setForm({ ...form, bank: e.target.value })} /></div>
            <div>
              <Label className="text-xs uppercase font-semibold">Payment Mode</Label>
              <Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="NEFT">NEFT</SelectItem><SelectItem value="RTGS">RTGS</SelectItem><SelectItem value="Cheque">Cheque</SelectItem><SelectItem value="UPI">UPI</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs uppercase font-semibold">Due Date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <PrimaryButton onClick={save} data-testid="save-pv">Save Voucher</PrimaryButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Mark as Paid</DialogTitle></DialogHeader>
          <div><Label className="text-xs uppercase font-semibold">UTR No.</Label><Input value={payForm.utr_no} onChange={(e) => setPayForm({ ...payForm, utr_no: e.target.value })} data-testid="pay-utr" /></div>
          <div><Label className="text-xs uppercase font-semibold">Cheque No.</Label><Input value={payForm.cheque_no} onChange={(e) => setPayForm({ ...payForm, cheque_no: e.target.value })} /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button>
            <PrimaryButton onClick={pay} data-testid="confirm-pay">Confirm Payment</PrimaryButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
